import React, { useEffect, useState } from "react";
import { CalendarDays } from "lucide-react";
import { es, enUS } from "date-fns/locale";
import { Calendar } from "./ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { useLang } from "../i18n";

const isoToDisplay = (iso) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || "");
  return m ? `${m[3]}/${m[2]}/${m[1]}` : "";
};
const isoToDate = (iso) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || "");
  return m ? new Date(+m[1], +m[2] - 1, +m[3]) : undefined;
};
const dateToIso = (dt) => `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
const displayToIso = (txt) => {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(txt);
  if (!m) return null;
  const dt = new Date(+m[3], +m[2] - 1, +m[1]);
  return dt.getFullYear() === +m[3] && dt.getMonth() === +m[2] - 1 && dt.getDate() === +m[1] ? dateToIso(dt) : null;
};
const mask = (raw) => {
  const digits = raw.replace(/\D/g, "").slice(0, 8);
  let out = digits.slice(0, 2);
  if (digits.length > 2) out += "/" + digits.slice(2, 4);
  if (digits.length > 4) out += "/" + digits.slice(4);
  return out;
};

export default function DateInput({ value, onChange, disabled = false, className = "ec-input", title, "data-testid": testid, ...rest }) {
  const { lang } = useLang();
  const [text, setText] = useState(isoToDisplay(value));
  const [open, setOpen] = useState(false);
  useEffect(() => { setText(isoToDisplay(value)); }, [value]);

  const emit = (iso) => onChange?.({ target: { value: iso } });
  const onText = (e) => {
    const v = mask(e.target.value);
    setText(v);
    if (v === "") emit("");
    else { const iso = displayToIso(v); if (iso) emit(iso); }
  };
  const onBlur = () => { if (text && !displayToIso(text)) setText(isoToDisplay(value)); };

  return (
    <div className="relative" data-testid={testid ? `${testid}-wrap` : undefined}>
      <input
        type="text" inputMode="numeric" placeholder="dd/mm/aaaa" maxLength={10} autoComplete="off"
        className={`${className} pr-10`} value={text} onChange={onText} onBlur={onBlur}
        disabled={disabled} title={title} data-testid={testid} {...rest}
      />
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button type="button" disabled={disabled} aria-label="calendar" data-testid={testid ? `${testid}-calendar-btn` : undefined}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-[#044CBC] disabled:opacity-40">
            <CalendarDays className="w-4 h-4" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0 bg-white" align="end" data-testid={testid ? `${testid}-calendar` : undefined}>
          <Calendar
            mode="single" locale={lang === "en" ? enUS : es} selected={isoToDate(value)}
            defaultMonth={isoToDate(value) || new Date()} captionLayout="dropdown-buttons" fromYear={1900} toYear={new Date().getFullYear() + 10}
            classNames={{
              caption: "flex justify-center pt-1 relative items-center",
              caption_label: "hidden",
              caption_dropdowns: "flex items-center gap-1 text-sm",
              dropdown: "text-sm font-semibold text-slate-800 bg-white border border-slate-200 rounded-md px-1.5 py-1 capitalize",
              dropdown_month: "", dropdown_year: "", vhidden: "hidden",
            }}
            onSelect={(dt) => { if (dt) emit(dateToIso(dt)); setOpen(false); }}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}
